import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { TrashIcon, PlusIcon, ExclamationTriangleIcon, CheckCircleIcon, ArrowPathIcon, LinkIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import { ru } from 'date-fns/locale/ru';
import 'react-datepicker/dist/react-datepicker.css';
import { differenceInDays, differenceInMonths, isValid, parseISO, addMonths, subDays, format } from 'date-fns';

registerLocale('ru', ru);

interface Client {
  id: number;
  name: string;
  director: string;
  currentDiscount: number;
}

interface Structure {
  id: number;
  code: string;
  type: string;
  location: string;
  dailyPrice: number;
  monthlyPrice: number;
  isLED: boolean;
}

interface ClientOption {
  value: number;
  label: string;
  clientData: { id: number; name: string; currentDiscount?: number; };
}

interface StructureOption {
  value: number;
  label: string;
  structureData: {
    id: number; code: string; type: string; location: string;
    currentDailyPrice: number | null; currentMonthlyPrice: number | null;
  };
}

interface OrderItemFormData {
  id?: number;
  structureId: number | null;
  startDate: string;
  endDate?: string;
  unitPrice?: number;
  isMonthlyRate?: boolean | null;
  discountPercentage?: number;
  notes?: string;
  status?: string;
  _structureCode?: string;
  _structureType?: string;
  _calculatedPrice?: number;
  _calculatedTotal?: number;
  quantity?: number;
}

interface OrderFormData {
  clientId: number | null;
  orderNumber?: string;
  status: 'reserved' | 'waitingPayment' | 'partiallyPaid' | 'fullyPaid' | 'completed' | 'cancelled';
  items: OrderItemFormData[];
  orderDate?: string;
  startDate?: string;
  endDate?: string;
  paymentTerms?: string;
  notes?: string;
  contractId?: number | null;
}

interface DisplayOrderData extends OrderFormData {
  totalAmount?: number;
  discountAmount?: number;
  finalAmount?: number;
  paidAmount?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface ConflictingOrderInfo {
  id: number;
  orderNumber?: string;
  status?: string;
  endDate?: string;
  clientName?: string | null;
}

interface AvailabilityStatus {
  isChecking: boolean;
  isAvailable: boolean | null;
  message: string | null;
  conflictingOrder?: ConflictingOrderInfo | null;
}

const ORDER_STATUSES: { [key: string]: string } = {
  reserved: 'Бронь',
  waitingPayment: 'Ожидает оплаты',
  partiallyPaid: 'Частично оплачен',
  fullyPaid: 'Полностью оплачен',
  completed: 'Завершен',
  cancelled: 'Отменен'
};

const OrderForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { can } = useAuth(); // Changed from isAllowed
  const isEditMode = !!id;

  const queryParams = new URLSearchParams(location.search);
  const clientIdFromQuery = queryParams.get('clientId');
  const quoteIdFromQuery = queryParams.get('quoteId');

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [structureOptions, setStructureOptions] = useState<StructureOption[]>([]);
  const [selectedClientOption, setSelectedClientOption] = useState<ClientOption | null>(null);
  const [displayOrderData, setDisplayOrderData] = useState<DisplayOrderData | null>(null);
  const [itemAvailability, setItemAvailability] = useState<{ [itemId: string]: AvailabilityStatus }>({});

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    getValues,
    trigger,
    formState: { errors }
  } = useForm<OrderFormData>({
    defaultValues: {
      clientId: clientIdFromQuery ? Number(clientIdFromQuery) : null,
      orderDate: new Date().toISOString().split('T')[0],
      startDate: '',
      endDate: '',
      status: 'reserved',
      paymentTerms: '100% предоплата',
      items: [],
      notes: '',
      contractId: null,
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items'
  });

  const watchClientId = watch('clientId');
  const watchItems = watch('items');

  useEffect(() => {
    if (watchItems && watchItems.length > 0) {
      try {
        const startDates = watchItems.map(item => item.startDate ? new Date(item.startDate).getTime() : Infinity).filter(d => !isNaN(d));
        const endDates = watchItems.map(item => item.endDate ? new Date(item.endDate).getTime() : -Infinity).filter(d => !isNaN(d));

        if (startDates.length > 0) {
          const earliestStart = new Date(Math.min(...startDates));
          if (!isNaN(earliestStart.getTime())) {
            setValue('startDate', earliestStart.toISOString().split('T')[0]);
          }
        }
        if (endDates.length > 0) {
          const latestEnd = new Date(Math.max(...endDates));
          if (!isNaN(latestEnd.getTime())) {
            setValue('endDate', latestEnd.toISOString().split('T')[0]);
          }
        }
      } catch (e) {
        console.error("Error calculating overall dates:", e);
      }
    }
  }, [watchItems, setValue]);

  useEffect(() => {
    const loadFormData = async () => {
      setLoading(true);
      setError(null);

      try {
        const structuresRes = await axios.get('/structures');
        const structureOpts = (structuresRes.data.structures || []).map((s: any) => ({
          value: s.id,
          label: `${s.code} - ${s.type} - ${s.location}`,
          structureData: { id: s.id, code: s.code, type: s.type, location: s.location, currentDailyPrice: s.currentDailyPrice, currentMonthlyPrice: s.currentMonthlyPrice }
        }));
        setStructureOptions(structureOpts);

        const copyFromId = queryParams.get('copyFrom');

        if (copyFromId) {
          // Handle copy from existing order
          const orderResponse = await axios.get(`/orders/${copyFromId}`);
          const orderData = orderResponse.data;

          if (orderData.clientId) {
            try {
              const clientOptionRes = await axios.get(`/clients/options?search=${orderData.Client?.name || ''}`);
              const foundOption = clientOptionRes.data.find((opt: ClientOption) => opt.value === orderData.clientId);
              if (foundOption) setSelectedClientOption(foundOption);
            } catch (clientErr) {
              console.error("Error fetching initial client option:", clientErr);
            }
          }

          setValue('clientId', orderData.clientId);
          setValue('orderNumber', ''); // Clear order number for new copy
          setValue('orderDate', new Date().toISOString().split('T')[0]); // Current date for new order
          setValue('startDate', orderData.startDate?.split('T')[0] || '');
          setValue('endDate', orderData.endDate?.split('T')[0] || '');
          setValue('status', 'reserved'); // Default status for new copy
          setValue('paymentTerms', orderData.paymentTerms || '');
          setValue('notes', `Копия заказа #${orderData.orderNumber || orderData.id}\n${orderData.notes || ''}`);
          setValue('contractId', orderData.contractId || null);

          if (orderData.OrderItems && orderData.OrderItems.length > 0) {
            setValue('items', orderData.OrderItems.map((item: any) => ({
              structureId: item.structureId,
              startDate: item.startDate?.split('T')[0],
              endDate: item.endDate?.split('T')[0],
              unitPrice: item.unitPrice,
              isMonthlyRate: item.isMonthlyRate,
              quantity: item.quantity,
              discountPercentage: item.discountPercentage,
              notes: item.notes,
              status: 'pending',
              _structureCode: item.Structure?.code,
              _structureType: item.Structure?.type,
              _calculatedPrice: item.totalPrice,
              _calculatedTotal: item.finalPrice
            } as OrderItemFormData)));
          }

          setLoading(false);

        }
        else if (isEditMode) {
          const orderResponse = await axios.get(`/orders/${id}`);
          const orderData = orderResponse.data;
          setDisplayOrderData(orderData);

          if (orderData.clientId) {
            try {
              const clientOptionRes = await axios.get(`/clients/options?search=${orderData.Client?.name || ''}`);
              const foundOption = clientOptionRes.data.find((opt: ClientOption) => opt.value === orderData.clientId);
              if (foundOption) setSelectedClientOption(foundOption);
            } catch (clientErr) {
              console.error("Error fetching initial client option:", clientErr);
            }
          }

          setValue('clientId', orderData.clientId);
          setValue('orderNumber', orderData.orderNumber);
          setValue('orderDate', orderData.orderDate?.split('T')[0] || new Date().toISOString().split('T')[0]);
          setValue('startDate', orderData.startDate?.split('T')[0] || '');
          setValue('endDate', orderData.endDate?.split('T')[0] || '');
          setValue('status', orderData.status);
          setValue('paymentTerms', orderData.paymentTerms || '');
          setValue('notes', orderData.notes || '');
          setValue('contractId', orderData.contractId || null);

          if (orderData.OrderItems && orderData.OrderItems.length > 0) {
            setValue('items', orderData.OrderItems.map((item: any) => ({
              id: item.id,
              structureId: item.structureId,
              startDate: item.startDate?.split('T')[0],
              endDate: item.endDate?.split('T')[0],
              unitPrice: item.unitPrice,
              isMonthlyRate: item.isMonthlyRate,
              quantity: item.quantity,
              discountPercentage: item.discountPercentage,
              notes: item.notes,
              status: item.status,
              _structureCode: item.Structure?.code,
              _structureType: item.Structure?.type,
              _calculatedPrice: item.totalPrice,
              _calculatedTotal: item.finalPrice
            } as OrderItemFormData)));
            console.log("Order items loaded:", orderData.OrderItems); // Debug log
          }
        }
        else if (quoteIdFromQuery) {
          const quoteResponse = await axios.get(`/quotes/${quoteIdFromQuery}`);
          const quoteData = quoteResponse.data;

          if (quoteData.clientId) {
            try {
              const clientOptionRes = await axios.get(`/clients/options?search=${quoteData.Client?.name || ''}`);
              const foundOption = clientOptionRes.data.find((opt: ClientOption) => opt.value === quoteData.clientId);
              if (foundOption) setSelectedClientOption(foundOption);
            } catch (clientErr) {
              console.error("Error fetching quote client option:", clientErr);
            }
          }
          setValue('clientId', quoteData.clientId);
          setValue('notes', `Создано из КП #${quoteData.quoteNumber || quoteData.id}. ${quoteData.notes || ''}`);
          setValue('paymentTerms', quoteData.termsAndConditions || '100% предоплата');

          if (quoteData.QuoteItems && quoteData.QuoteItems.length > 0) {
            setValue('items', quoteData.QuoteItems.map((item: any) => ({
              structureId: item.structureId,
              startDate: item.startDate?.split('T')[0],
              endDate: item.endDate?.split('T')[0],
              unitPrice: item.unitPrice,
              isMonthlyRate: item.isMonthlyRate,
              quantity: item.quantity,
              discountPercentage: item.discountPercentage,
              notes: item.notes,
              status: 'waitingPayment',
              _structureCode: item.Structure?.code,
              _structureType: item.Structure?.type,
              _calculatedPrice: item.totalPrice,
              _calculatedTotal: item.finalPrice
            } as OrderItemFormData)));

          }
        } else if (clientIdFromQuery) {
          try {
            const clientOptionRes = await axios.get(`/clients/options?search=&clientId=${clientIdFromQuery}`);
            const foundOption = clientOptionRes.data.find((opt: ClientOption) => opt.value === Number(clientIdFromQuery));
            if (foundOption) {
              setSelectedClientOption(foundOption);
              setValue('clientId', Number(clientIdFromQuery));
            }
          } catch (clientErr) {
            console.error("Error fetching initial client option:", clientErr);
          }
        }
      } catch (err) {
        console.error('Error loading form data:', err);
        setError('Ошибка при загрузке данных');
      } finally {
        setLoading(false);
      }
    };

    loadFormData();
  }, [id, isEditMode, clientIdFromQuery, quoteIdFromQuery, setValue]);

  useEffect(() => {
    if (selectedClientOption && fields.length > 0) {
      const clientDiscount = selectedClientOption.clientData?.currentDiscount ?? 0;
      fields.forEach((field, index) => {
        const currentItem = watchItems[index];
        if (currentItem?.isMonthlyRate !== null) {
          setValue(`items.${index}.discountPercentage`, clientDiscount, { shouldDirty: true });
        }
      });
    }
  }, [selectedClientOption, fields, setValue, watchItems]);

  const handleAddItem = () => {
    const today = new Date();
    const thirtyDaysLater = new Date(today);
    thirtyDaysLater.setDate(today.getDate() + 30);
    
    const clientDiscount = selectedClientOption?.clientData?.currentDiscount ?? 0;

    append({
      structureId: null,
      startDate: today.toISOString().split('T')[0],
      endDate: thirtyDaysLater.toISOString().split('T')[0],
      unitPrice: undefined,
      isMonthlyRate: true,
      discountPercentage: clientDiscount,
      notes: '',
      status: 'pending'
    } as OrderItemFormData);
  };

  const onSubmit = async (formData: OrderFormData) => {
    setSubmitting(true);
    setError(null);

    const submissionData = {
      clientId: formData.clientId,
      status: formData.status,
      notes: formData.notes,
      orderDate: formData.orderDate,
      paymentTerms: formData.paymentTerms,
      contractId: formData.contractId || null,
      items: formData.items.map(item => ({
        id: isEditMode ? item.id : undefined,
        structureId: item.structureId,
        startDate: item.startDate,
        endDate: item.endDate,
        unitPrice: item.unitPrice,
        isMonthlyRate: item.isMonthlyRate,
        discountPercentage: item.discountPercentage,
        notes: item.notes,
        status: item.status,
        quantity: item.quantity
      })).filter(item => item.structureId),
    };

    if (!submissionData.clientId) {
      setError('Пожалуйста, выберите клиента.');
      setSubmitting(false);
      return;
    }
    if (submissionData.items.length === 0) {
      setError('Пожалуйста, добавьте хотя бы одну позицию в заказ.');
      setSubmitting(false);
      return;
    }

    try {
      let response;
      if (isEditMode) {
        response = await axios.put(`/orders/${id}`, submissionData);
      } else {
        response = await axios.post('/orders', submissionData);
      }

      const orderId = response.data.id || id;
      navigate(`/orders/${orderId}`);
    } catch (err: any) {
      console.error('Error saving order:', err);
      setError(err.response?.data?.message || 'Ошибка при сохранении заказа');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number | undefined | null): string => {
    if (amount === undefined || amount === null || isNaN(amount)) return '-';
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 2
    }).format(amount);
  };

  const displayTotals = useMemo(() => {
    return {
      totalAmount: displayOrderData?.totalAmount,
      discountAmount: displayOrderData?.discountAmount,
      finalAmount: displayOrderData?.finalAmount
    }
  }, [displayOrderData]);

  const calculateUnitPrice = (structureData: StructureOption['structureData'] | undefined, isMonthly: boolean | undefined, isService: boolean | undefined): number => {
    if (!structureData) return 0;
    
    if (isService) {
      return structureData.currentMonthlyPrice ?? 0;
    }
    
    return isMonthly
      ? (structureData.currentMonthlyPrice ?? 0)
      : (structureData.currentDailyPrice ?? 0);
  };

  const handleCheckAvailability = useCallback(async (index: number) => {
    const item = getValues(`items.${index}`);
    const itemId = fields[index]?.id;

    if (!item || !item.structureId || !item.startDate || !item.endDate || !itemId) {
      setItemAvailability(prev => ({
        ...prev,
        [itemId]: { isChecking: false, isAvailable: null, message: null, conflictingOrder: null }
      }));
      return;
    }

    setItemAvailability(prev => ({
      ...prev,
      [itemId]: { isChecking: true, isAvailable: null, message: null, conflictingOrder: null }
    }));

    try {
      const params: any = {
        structureId: item.structureId,
        startDate: item.startDate,
        endDate: item.endDate,
      };
      if (isEditMode && id) {
        params.excludeOrderId = id;
      }

      const response = await axios.get<{ isAvailable: boolean; message: string | null; conflictingOrder?: ConflictingOrderInfo | null }>(
        '/orders/check-availability',
        { params }
      );

      setItemAvailability(prev => ({
        ...prev,
        [itemId]: {
          isChecking: false,
          isAvailable: response.data.isAvailable,
          message: response.data.isAvailable ? null : (response.data.message || 'Конструкция занята в этот период'),
          conflictingOrder: response.data.conflictingOrder
        }
      }));

    } catch (error) {
      console.error('Error checking availability:', error);
      setItemAvailability(prev => ({
        ...prev,
        [itemId]: {
          isChecking: false,
          isAvailable: null,
          message: 'Ошибка проверки доступности',
          conflictingOrder: null
        }
      }));
    }
  }, [getValues, isEditMode, id, fields]);

  const triggerAvailabilityCheck = (index: number) => {
    const item = watch(`items.${index}`);
    if (item?.structureId && item?.startDate && item?.endDate) {
      handleCheckAvailability(index);
    }
  };

  // Helper function to calculate final price for a single item
  const calculateItemFinalPrice = (item: OrderItemFormData | undefined): number => {
    if (!item) return 0;

    console.log("Calculating final price for item:", item); // Debug log

    const price = item.unitPrice ?? 0;
    const discount = (item.discountPercentage ?? 0) / 100;
    let quantity = 0;

    if (item.isMonthlyRate === true) {
      quantity = item.quantity ?? 0;
    } else if (item.isMonthlyRate === false)  {
      const startDate = item.startDate ? parseISO(item.startDate) : null;
      const endDate = item.endDate ? parseISO(item.endDate) : null;
      if (startDate && endDate && isValid(startDate) && isValid(endDate) && endDate >= startDate) {
        quantity = differenceInDays(endDate, startDate) + 1; // Inclusive days
      }
    } else{
      quantity = item.quantity ?? 0;
    }

    const baseTotal = price * quantity;
    const discountedTotal = baseTotal * (1 - discount);
    return isNaN(discountedTotal) ? 0 : discountedTotal;
  };

  // Calculate the total for the items table footer using the helper
  const itemsTotal = (watchItems || []).reduce((sum, item) => {
    return sum + calculateItemFinalPrice(item);
  }, 0);

  // Make sure relevant fields trigger validation/recalculation
  const handlePriceOrDiscountChange = (index: number) => {
    trigger(`items.${index}.unitPrice`);
    trigger(`items.${index}.discountPercentage`);
    // No need to explicitly trigger total recalc, it happens on re-render
  };

  // Function to calculate and update the end date based on start date and quantity (for monthly)
  const updateCalculatedEndDate = useCallback((index: number) => {
    const startDateStr = getValues(`items.${index}.startDate`);
    const quantity = getValues(`items.${index}.quantity`);
    const isMonthly = getValues(`items.${index}.isMonthlyRate`);

    if (isMonthly && startDateStr && quantity && quantity > 0 && quantity <= 1200) {
      try {
        const startDate = parseISO(startDateStr);
        if (isValid(startDate)) {
          const endDateRough = addMonths(startDate, quantity);
          const calculatedEndDate = subDays(endDateRough, 1);
          setValue(`items.${index}.endDate`, format(calculatedEndDate, 'yyyy-MM-dd'), {
            shouldValidate: true, // Optionally re-validate dependent fields
            shouldDirty: true
          });
          // Re-trigger availability check as end date changed
          triggerAvailabilityCheck(index);
        } else {
          setValue(`items.${index}.endDate`, ''); // Clear if start date invalid
        }
      } catch (e) {
        console.error("Error calculating end date:", e);
        setValue(`items.${index}.endDate`, ''); // Clear on error
      }
    } else if (!isMonthly) {
      // If switching back to daily, end date is handled by DatePicker, 
      // but we might still trigger availability if start date exists.
      const endDate = getValues(`items.${index}.endDate`);
      if (startDateStr && endDate) {
        triggerAvailabilityCheck(index);
      }
    }
  }, [getValues, setValue, triggerAvailabilityCheck]); // Include triggerAvailabilityCheck

  // Function to load client options async
  const loadClientOptions = async (inputValue: string): Promise<ClientOption[]> => {
    try {
      const response = await axios.get(`/clients/options?search=${inputValue}`);
      return response.data;
    } catch (error) {
      console.error('Error loading client options:', error);
      return [];
    }
  };

  if (isEditMode ? !can('orders.edit') : !can('orders.create')) { // Changed from isAllowed
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-red-600">У вас нет прав доступа к этой странице</p>
      </div>
    );
  }

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      <PageHeader
        title={isEditMode ? `Редактировать Заказ #${displayOrderData?.orderNumber || id}` : 'Создать Заказ'}
        backLink="/orders"
      />

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Основная информация</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="clientId" className="form-label">Клиент *</label>
              <Controller
                name="clientId"
                control={control}
                rules={{ required: 'Клиент обязателен' }}
                render={({ field, fieldState }) => (
                  <AsyncSelect<ClientOption>
                    inputId="clientId"
                    cacheOptions
                    defaultOptions // Load initial options (empty search)
                    loadOptions={loadClientOptions}
                    value={selectedClientOption}
                    onChange={(option: ClientOption | null) => {
                      field.onChange(option ? option.value : null);
                      setSelectedClientOption(option);
                    }}
                    //isDisabled={isEditMode} // Keep disabled in edit mode if needed
                    placeholder="Начните вводить имя клиента..."
                    classNamePrefix="react-select"
                    noOptionsMessage={({ inputValue }) => inputValue ? "Клиенты не найдены" : "Начните вводить..."}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: fieldState.error ? '#ef4444' : base.borderColor,
                        backgroundColor: isEditMode ? '#f3f4f6' : base.backgroundColor // Optional: grey out if disabled
                      }),
                      menu: (base) => ({ ...base, zIndex: 30 })
                    }}
                  />
                )}
              />
              {errors.clientId && <p className="mt-1 text-sm text-red-600">{errors.clientId.message}</p>}
            </div>

            <div>
              <label htmlFor="orderDate" className="form-label">Дата Заказа</label>
              <input
                id="orderDate"
                type="date"
                className={`form-input ${errors.orderDate ? 'border-red-500' : ''}`}
                {...register('orderDate')}
              />
            </div>

            <div>
              <label htmlFor="status" className="form-label">Статус *</label>
              <select
                id="status"
                className={`form-input ${errors.status ? 'border-red-500' : ''}`}
                {...register('status', { required: 'Статус обязателен' })}
              >
                {Object.entries(ORDER_STATUSES).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              {errors.status && <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>}
            </div>

            <div>
              <label htmlFor="displayStartDate" className="form-label">Начало периода (Общее)</label>
              <input
                id="displayStartDate"
                type="date"
                className="form-input bg-gray-100"
                value={watch('startDate') || ''}
                readOnly
              />
            </div>

            <div>
              <label htmlFor="displayEndDate" className="form-label">Конец периода (Общее)</label>
              <input
                id="displayEndDate"
                type="date"
                className="form-input bg-gray-100"
                value={watch('endDate') || ''}
                readOnly
              />
            </div>

            <div>
              <label htmlFor="contractId" className="form-label">Договор (ID)</label>
              <input
                id="contractId"
                type="text"
                className="form-input"
                placeholder="ID договора"
                {...register('contractId', { valueAsNumber: true })}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Позиции заказа</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">Конструкция</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">Начало</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">Период/Кол-во</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">Цена ед. / Тариф</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">Скидка %</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-[150px]">Итого</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Заметки</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-[100px]">Статус</th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-[50px]"></th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {fields.map((field, index) => {
                  const availabilityStatus = itemAvailability[field.id];
                  const conflict = availabilityStatus?.conflictingOrder;
                  const rowClass = availabilityStatus?.isAvailable === false ? 'bg-red-50' : '';
                  const isMonthly = watchItems[index]?.isMonthlyRate === true;
                  const isService = watchItems[index]?.isMonthlyRate === null;
                  const isDaily = watchItems[index]?.isMonthlyRate === false;

                  return (
                    <tr key={field.id} className={rowClass}>
                      <td className="px-3 py-2 align-top relative">
                        <Controller
                          name={`items.${index}.structureId`}
                          control={control}
                          rules={{ required: 'Выберите конструкцию' }}
                          render={({ field: { onChange, value }, fieldState: { error } }) => (
                            <Select<StructureOption>
                              inputId={`items.${index}.structureId`}
                              options={structureOptions}
                              value={structureOptions.find(opt => opt.value === value) || null}
                              onChange={(option: StructureOption | null) => {
                                onChange(option ? option.value : null);

                                const clientDiscount = selectedClientOption?.clientData?.currentDiscount ?? 0;
                                const structureData = option?.structureData;

                                const currentIsMonthly = getValues(`items.${index}.isMonthlyRate`);
                                const newIsMonthly = typeof currentIsMonthly === 'boolean' ? currentIsMonthly : true;
                                console.log("New isMonthly:", newIsMonthly, currentIsMonthly); // Debug log

                                const newUnitPrice = calculateUnitPrice(structureData, newIsMonthly, undefined);

                                setValue(`items.${index}.unitPrice`, newUnitPrice, { shouldDirty: true });
                                setValue(`items.${index}.isMonthlyRate`, newIsMonthly, { shouldDirty: true });
                                setValue(`items.${index}.discountPercentage`, clientDiscount, { shouldDirty: true });
                                setValue(`items.${index}._structureCode`, structureData?.code || '');
                                setValue(`items.${index}._structureType`, structureData?.type || '');

                                trigger(`items.${index}.unitPrice`);
                                trigger(`items.${index}.discountPercentage`);

                                handleCheckAvailability(index);
                              }}
                              placeholder="Выберите..."
                              classNamePrefix="react-select-item"
                              noOptionsMessage={() => 'Конструкции не найдены'}
                              menuPortalTarget={document.body}
                              styles={{
                                menuPortal: base => ({ ...base, zIndex: 9999 }),
                                control: (base: any) => ({ ...base, minHeight: '38px', height: '38px', fontSize: '0.875rem' }),
                                valueContainer: (base: any) => ({ ...base, height: '38px', padding: '0 6px' }),
                                indicatorsContainer: (base: any) => ({ ...base, height: '38px' })
                              }}
                            />
                          )}
                        />
                        {errors.items?.[index]?.structureId && (
                          <p className="text-red-500 text-xs mt-1">{errors.items[index]?.structureId?.message}</p>
                        )}
                        <div className="text-xs mt-1 space-y-0.5">
                          <div className="flex items-center space-x-1">
                            {availabilityStatus?.isChecking && (
                              <><ArrowPathIcon className="h-3 w-3 text-gray-400 animate-spin flex-shrink-0" /><span className="text-gray-500">Проверка...</span></>
                            )}
                            {availabilityStatus?.isAvailable === true && (
                              <><CheckCircleIcon className="h-3 w-3 text-green-500 flex-shrink-0" /><span className="text-green-600">Конструкция свободна</span></>
                            )}
                            {availabilityStatus?.isAvailable === false && (
                              <>
                                <ExclamationTriangleIcon className="h-3 w-3 text-red-500 flex-shrink-0" />
                                <span className="text-red-600">{availabilityStatus.message || 'Конструкция занята'}</span>
                                {conflict && (
                                  <Link
                                    to={`/orders/${conflict.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title={`Перейти к заказу #${conflict.orderNumber || conflict.id}`}
                                    className="ml-1 text-blue-600 hover:text-blue-800 hover:underline flex-shrink-0"
                                  >
                                    <LinkIcon className="h-3 w-3" />
                                  </Link>
                                )}
                              </>
                            )}
                            {availabilityStatus?.isAvailable === null && !availabilityStatus.isChecking && availabilityStatus.message && (
                              <><ExclamationTriangleIcon className="h-3 w-3 text-yellow-500 flex-shrink-0" /><span className="text-yellow-600">{availabilityStatus.message}</span></>
                            )}
                          </div>
                          {conflict && (
                            <div className="pl-4 text-gray-500">
                              <span>Заказ: <span className="font-medium">#{conflict.orderNumber || conflict.id}</span></span><br />
                              <span>Клиент: <span className="font-medium">{conflict.clientName || '-'}</span></span><br />
                              <span>Статус: <span className="font-medium">{ORDER_STATUSES[conflict.status || ''] || conflict.status || '-'}</span></span><br />
                              <span>Освободится: <span className="font-medium">{conflict.endDate ? format(parseISO(conflict.endDate), 'dd.MM.yyyy') : '-'}</span></span>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top">
                        <Controller
                          control={control}
                          name={`items.${index}.startDate`}
                          rules={{ required: "Дата обязательна" }}
                          render={({ field, fieldState: { error } }) => (
                            <DatePicker
                              selected={field.value ? new Date(field.value) : null}
                              onChange={(date) => {
                                const newDate = date ? date.toISOString().split('T')[0] : null;
                                field.onChange(newDate);
                                trigger(`items.${index}.endDate`);
                                updateCalculatedEndDate(index);
                              }}
                              dateFormat="dd.MM.yyyy"
                              locale={ru}
                              className={`form-input w-full ${error ? 'border-red-500' : ''}`}
                              placeholderText="ДД.ММ.ГГГГ"
                              popperPlacement="top-start"
                              popperClassName="z-[1000]"
                            />
                          )}
                        />
                        {errors.items?.[index]?.startDate && <p className="mt-1 text-xs text-red-600">{errors.items?.[index]?.startDate?.message}</p>}
                      </td>
                      <td className="px-3 py-2 align-top">
                        {(isMonthly || isService) ? (
                          <div className="space-y-1">
                            <input
                              type="number"
                              min="1"
                              max="1200"
                              step="1"
                              className={`form-input w-full ${errors.items?.[index]?.quantity ? 'border-red-500' : ''}`}
                              placeholder={isMonthly ? "Кол-во мес." : "Кол-во ед."}
                              {...register(`items.${index}.quantity` , {
                                required: (isMonthly || isService) ? 'Кол-во обязательно' : false,
                                min: { value: 1, message: 'Мин. 1' },
                                max: { value: 1200, message: 'Макс. 1200' },
                                valueAsNumber: true,
                                onChange: () => {
                                  if (isMonthly) updateCalculatedEndDate(index);
                                  triggerAvailabilityCheck(index);
                                }
                              })}
                            />
                            {errors.items?.[index]?.quantity && (
                              <p className="mt-1 text-xs text-red-600">{errors.items?.[index]?.quantity?.message}</p>
                            )}
                            {isMonthly && (
                            <input
                              type="text"
                              className="form-input w-full bg-gray-100 text-sm text-gray-600 cursor-not-allowed"
                              value={watchItems[index]?.endDate ? format(parseISO(watchItems[index].endDate!), 'dd.MM.yyyy') : '-'}
                              readOnly
                              placeholder="Расчетная дата конца"
                            />
                            )}
                          </div>
                        ) : (
                          <>
                            <Controller
                              control={control}
                              name={`items.${index}.endDate`}
                              rules={{
                                required: isDaily ? 'Дата конца обязательна' : false,
                                validate: value => {
                                  if (!isDaily) return true;
                                  const startDate = getValues(`items.${index}.startDate`);
                                  return !startDate || !value || new Date(value) >= new Date(startDate) || 'Конец не раньше начала';
                                }
                              }}
                              render={({ field: endDateField, fieldState: { error: endDateError } }) => (
                                <DatePicker
                                  selected={endDateField.value ? new Date(endDateField.value) : null}
                                  onChange={(date) => {
                                    const newDate = date ? date.toISOString().split('T')[0] : null;
                                    endDateField.onChange(newDate);
                                    trigger(`items.${index}.startDate`);
                                    triggerAvailabilityCheck(index);
                                  }}
                                  minDate={watchItems?.[index]?.startDate ? new Date(watchItems[index].startDate) : undefined}
                                  dateFormat="dd.MM.yyyy"
                                  locale={ru}
                                  className={`form-input w-full ${endDateError ? 'border-red-500' : ''}`}
                                  placeholderText={"ДД.ММ.ГГГГ"}
                                  popperPlacement="top-start"
                                  popperClassName="z-[1000]"
                                />
                              )}
                            />
                            {errors.items?.[index]?.endDate && (
                              <p className="mt-1 text-xs text-red-600">{errors.items?.[index]?.endDate?.message}</p>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <div className="flex flex-col items-end">
                          <input
                            type="number"
                            step="0.01"
                            className="form-input text-right w-full"
                            {...register(`items.${index}.unitPrice` as const, {
                              valueAsNumber: true,
                              required: 'Цена обязательна',
                              min: { value: 0, message: 'Цена не может быть отрицательной' },
                              onChange: () => handlePriceOrDiscountChange(index)
                            })}
                            placeholder="Цена"
                          />
                          <select
                            className="form-input text-xs w-full"
                            {...register(`items.${index}.isMonthlyRate` as const, {
                              setValueAs: (value) => {
                                if (value === 'monthly') return true;
                                if (value === 'daily') return false;
                                if (value === 'service') return null;
                                return value;
                              },
                              onChange: (e) => {
                                const isMonthlyNow = e.target.value === 'monthly';
                                const isServiceNow = e.target.value === 'service';
                                const isDailyNow = e.target.value === 'daily';
                                
                                console.log("Rate change:", e.target.value, "isService:", isServiceNow, "isMonthly:", isMonthlyNow);
                                
                                const clientDiscount = selectedClientOption?.clientData?.currentDiscount ?? 0;
                                
                                if (isServiceNow) {
                                  setValue(`items.${index}.discountPercentage`, 0, { shouldDirty: true });
                                  
                                  setValue(`items.${index}.quantity`, 1, { shouldDirty: true });
                                  
                                  const startDate = getValues(`items.${index}.startDate`);
                                  if (startDate) {
                                    setValue(`items.${index}.endDate`, startDate, { shouldValidate: false });
                                  }
                                } else {
                                  setValue(`items.${index}.discountPercentage`, clientDiscount, { shouldDirty: true });
                                  
                                  if (isMonthlyNow) {
                                    setValue(`items.${index}.endDate`, '', { shouldValidate: false });
                                    setValue(`items.${index}.quantity`, 1, { shouldDirty: true });
                                    updateCalculatedEndDate(index);
                                  } else {
                                    const startDate = getValues(`items.${index}.startDate`);
                                    let defaultEndDate = '';
                                    if (startDate) {
                                      const thirtyDaysLater = new Date(parseISO(startDate));
                                      thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
                                      defaultEndDate = thirtyDaysLater.toISOString().split('T')[0];
                                    }
                                    setValue(`items.${index}.endDate`, defaultEndDate, { shouldValidate: true });
                                    if (defaultEndDate) triggerAvailabilityCheck(index);
                                  }
                                }
                                const currentStructureId = getValues(`items.${index}.structureId`);
                                const structureOption = structureOptions.find(opt => opt.value === currentStructureId);
                                const unitPrice = calculateUnitPrice(structureOption?.structureData, isMonthlyNow, isServiceNow);
                                setValue(`items.${index}.unitPrice`, unitPrice, { shouldDirty: true });
                                if (isMonthlyNow || isServiceNow) {
                                  trigger(`items.${index}.quantity`);
                                } else {
                                  trigger(`items.${index}.endDate`);
                                }
                              }
                            })}
                            defaultValue={watchItems[index]?.isMonthlyRate === false ? 'false' : 'true'}
                          >
                            <option value="monthly">в месяц</option>
                            <option value="daily">в день</option>
                            <option value="service">услуга</option>
                          </select>
                          {errors.items?.[index]?.unitPrice && <p className="text-red-600 text-xs mt-1">{errors.items?.[index]?.unitPrice?.message}</p>}
                        </div>
                      </td>
                      <td className="px-3 py-2 align-top text-right">
                        <input
                          type="number"
                          step="0.01"
                          className={`form-input text-right w-full ${isService ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                          {...register(`items.${index}.discountPercentage` as const, {
                            valueAsNumber: true,
                            min: { value: 0, message: 'Скидка >= 0' },
                            max: { value: 100, message: 'Скидка <= 100' },
                            onChange: () => handlePriceOrDiscountChange(index)
                          })}
                          placeholder="Скидка %"
                          disabled={isService}
                        />
                        {errors.items?.[index]?.discountPercentage && <p className="text-red-600 text-xs mt-1">{errors.items?.[index]?.discountPercentage?.message}</p>}
                      </td>
                      <td className="px-3 py-2 align-top text-right text-sm text-gray-700">
                        {formatCurrency(calculateItemFinalPrice(watchItems[index]))}
                      </td>
                      <td className="px-3 py-2 align-top">
                        <textarea
                          rows={1}
                          className="form-input text-xs w-full"
                          placeholder="Заметка..."
                          {...register(`items.${index}.notes` as const)}
                        />
                      </td>
                      <td className="px-3 py-2 align-top">
                        <select
                          className="form-input text-xs w-full"
                          {...register(`items.${index}.status` as const)}
                        >
                          {Object.entries(ORDER_STATUSES).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2 align-top text-center">
                        <button
                          type="button"
                          onClick={() => remove(index)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                <tr>
                  <td colSpan={5} className="px-3 py-3 text-right text-sm font-medium text-gray-700 uppercase">
                    Итого по позициям:
                  </td>
                  <td className="px-3 py-3 text-right text-sm font-semibold text-gray-900">
                    {formatCurrency(itemsTotal)}
                  </td>
                  <td colSpan={3} className="px-3 py-3"></td>
                </tr>
              </tfoot>
            </table>
          </div>
          <div className="mt-4">
            <button
              type="button"
              onClick={handleAddItem}
              className="btn-secondary inline-flex items-center text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Добавить позицию
            </button>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Дополнительные параметры</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="paymentTerms" className="form-label">Условия оплаты</label>
              <input
                id="paymentTerms"
                type="text"
                className={`form-input ${errors.paymentTerms ? 'border-red-500' : ''}`}
                {...register('paymentTerms')}
              />
            </div>

            <div>
              <label htmlFor="notes" className="form-label">Примечания</label>
              <textarea
                id="notes"
                className={`form-input ${errors.notes ? 'border-red-500' : ''}`}
                {...register('notes')}
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold mb-4">Итоги</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label htmlFor="totalAmount" className="form-label">Общая стоимость</label>
              <input
                id="totalAmount"
                type="text"
                className="form-input"
                value={formatCurrency(displayTotals?.totalAmount)}
                readOnly
              />
            </div>

            <div>
              <label htmlFor="discountAmount" className="form-label">Скидка</label>
              <input
                id="discountAmount"
                type="text"
                className="form-input"
                value={formatCurrency(displayTotals?.discountAmount)}
                readOnly
              />
            </div>

            <div>
              <label htmlFor="finalAmount" className="form-label">Итого</label>
              <input
                id="finalAmount"
                type="text"
                className="form-input"
                value={formatCurrency(displayTotals?.finalAmount)}
                readOnly
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="btn-primary"
            >
              {submitting ? <LoadingSpinner /> : (isEditMode ? 'Сохранить изменения' : 'Создать Заказ')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default OrderForm;